import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { channel_options } from "common/filters";
import { Img } from "components";
import { ChannelOptions } from "pages/Leads/lead";
import { useCallback, useMemo, useState } from "react";

interface InteractionCardProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  isPaginationVisible?: boolean;
  isPageCountVisible?: boolean;
  defaultSorting?: { id: string; desc: boolean }[];
}

// Memoized utility functions
const parseCreatedAt = (dateStr: string): number => {
  if (!dateStr) return 0;

  if (dateStr.startsWith("Today")) {
    const timePart = dateStr.split("|")[1]?.trim() || "";
    const now = new Date();
    const [time, meridian] = timePart.split(" ");
    if (!time) return now.getTime();
    let [hours, minutes] = time.split(":").map(Number);
    if (meridian?.toLowerCase() === "pm" && hours < 12) hours += 12;
    if (meridian?.toLowerCase() === "am" && hours === 12) hours = 0;
    const date = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hours,
      minutes
    );
    return date.getTime();
  }

  const parts = dateStr.split("|").map((s) => s.trim());
  if (parts.length < 2) return 0;
  const [datePart, timePart] = parts;

  const dateObj = new Date(`${datePart} ${timePart}`);
  return isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
};

const formatCreatedAt = (dateStr: string): string => {
  const timestamp = parseCreatedAt(dateStr);
  if (!timestamp) return "Invalid date";

  const date = new Date(timestamp);

  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime = date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  return `${formattedDate.replace(/ /g, "-")} | ${formattedTime}`;
};

// Memoized SVG components
const QuotationIcon = () => (
  <svg
    width="16"
    height="20"
    viewBox="0 0 16 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Open quotation"
  >
    <path
      d="M10.932 0.15457C10.8403 0.0579688 10.7146 0 10.5843 0H2.78234C1.34262 0 0.154297 1.18359 0.154297 2.62313V17.3767C0.154297 18.8164 1.34266 20 2.7823 20H13.3716C14.8113 20 15.9996 18.8164 15.9996 17.3767V5.66172C15.9996 5.53617 15.9416 5.41547 15.8596 5.32359L10.932 0.15457Z"
      fill="#165FE3"
    />
    <path
      d="M12.052 15.6968H4.10027C3.83465 15.6968 3.61719 15.914 3.61719 16.1798C3.61719 16.4455 3.83461 16.6629 4.10027 16.6629H12.0568C12.3225 16.6629 12.5399 16.4455 12.5399 16.1798C12.5399 15.914 12.3225 15.6968 12.052 15.6968Z"
      fill="#165FE3"
    />
  </svg>
);

const SelfieIcon = () => (
  <svg
    width="24"
    height="23"
    viewBox="0 0 24 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Download selfie"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.2079 17.6048C21.2782 17.6751 21.3177 17.7705 21.3177 17.87C21.3177 17.9694 21.2782 18.0648 21.2079 18.1351L19.6156 19.7274C19.5453 19.7976 19.4499 19.837 19.3505 19.837C19.251 19.837 19.1557 19.7976 19.0853 19.7274L17.493 18.1351C17.4226 18.0648 17.3831 17.9694 17.3832 17.8699C17.3832 17.7704 17.4227 17.675 17.493 17.6047C17.5634 17.5344 17.6588 17.4949 17.7583 17.4949C17.8577 17.4949 17.9531 17.5345 18.0234 17.6048L18.9755 18.5568V15.5437C18.9755 15.4443 19.015 15.3489 19.0853 15.2786C19.1556 15.2082 19.251 15.1687 19.3505 15.1687C19.4499 15.1687 19.5453 15.2082 19.6156 15.2786C19.686 15.3489 19.7255 15.4443 19.7255 15.5437V18.5568L20.6776 17.6048C20.748 17.5346 20.8434 17.4952 20.9428 17.4952C21.0422 17.4952 21.1375 17.5346 21.2079 17.6048H21.2079ZM23.2213 17.9728C23.3457 16.9462 23.0573 15.9123 22.4195 15.0983C21.7817 14.2843 20.8468 13.757 19.8203 13.6322C19.6618 13.6129 19.5022 13.6031 19.3425 13.603C18.5917 13.6044 17.8572 13.8227 17.2274 14.2314C16.5975 14.6402 16.0991 15.2221 15.7919 15.9073C15.4848 16.5924 15.382 17.3517 15.4959 18.0939C15.6097 18.836 15.9355 19.5295 16.4339 20.0911C16.9324 20.6526 17.5824 21.0583 18.3058 21.2594C19.0292 21.4606 19.7953 21.4485 20.5121 21.2248C21.2288 21.0011 21.8658 20.5752 22.3463 19.9983C22.8269 19.4214 23.1307 18.718 23.2213 17.9726V17.9728ZM2.94998 19.515H15.1577C14.7973 18.7632 14.6456 17.9283 14.7185 17.0978C14.7914 16.2672 15.0862 15.4715 15.5721 14.794C16.058 14.1165 16.7171 13.5821 17.4804 13.2467C18.2437 12.9113 19.0832 12.7873 19.9109 12.8876C20.0698 12.9071 20.2277 12.9345 20.384 12.9697V10.0166L17.9141 7.54684L12.7912 11.24L14.3185 12.3106C14.3597 12.3385 14.3949 12.3743 14.4221 12.4159C14.4492 12.4576 14.4679 12.5042 14.4769 12.5532C14.4859 12.6021 14.485 12.6523 14.4744 12.7009C14.4638 12.7495 14.4437 12.7955 14.4151 12.8362C14.3866 12.8769 14.3502 12.9116 14.3081 12.9381C14.2661 12.9647 14.2191 12.9826 14.1701 12.9908C14.121 12.999 14.0708 12.9974 14.0224 12.986C13.974 12.9747 13.9283 12.9538 13.888 12.9246L6.19116 7.52931L0.754078 12.1268C0.752719 12.1279 0.751125 12.129 0.749766 12.1301V17.3145C0.750349 17.8978 0.982338 18.4572 1.39483 18.8697C1.80732 19.2822 2.36661 19.5142 2.94998 19.5149V19.515ZM2.94998 1.59747H18.1836C18.7669 1.59813 19.3261 1.83014 19.7386 2.24261C20.1511 2.65508 20.3831 3.21432 20.3838 3.79764V8.95623L18.2186 6.79117C18.1557 6.72819 18.0724 6.68967 17.9836 6.68249C17.8948 6.67532 17.8064 6.69997 17.7342 6.75203L12.141 10.7842L6.3847 6.74917C6.31685 6.70167 6.23519 6.67795 6.15244 6.68171C6.0697 6.68546 5.99052 6.71649 5.92725 6.76994L0.75 11.1482V3.79773C0.75067 3.21445 0.982662 2.65525 1.39509 2.24278C1.80752 1.83032 2.3667 1.59828 2.94998 1.59756V1.59747ZM23.01 14.6359C22.5188 14.0054 21.8723 13.5132 21.1337 13.2075V3.79773C21.1328 3.01559 20.8217 2.26575 20.2686 1.7127C19.7156 1.15964 18.9657 0.848549 18.1836 0.847656H2.94998C2.16787 0.848599 1.41807 1.15972 0.865039 1.71276C0.312011 2.26581 0.000917972 3.01562 0 3.79773L0 17.3147C0.0007446 18.0969 0.311806 18.8469 0.864916 19.4C1.41803 19.9531 2.16799 20.2642 2.95022 20.265H15.6112C15.6371 20.3007 15.6638 20.3358 15.6911 20.3705C16.4543 21.3329 17.5674 21.9542 18.7872 22.0986C20.007 22.2431 21.2343 21.8989 22.2012 21.1414C23.1681 20.3838 23.7958 19.2744 23.9474 18.0555C24.0989 16.8365 23.762 15.6072 23.01 14.6359ZM11.8242 4.26283C12.1642 4.26283 12.4966 4.36364 12.7792 4.55253C13.0619 4.74141 13.2823 5.00988 13.4124 5.32398C13.5425 5.63809 13.5765 5.98372 13.5102 6.31717C13.4439 6.65062 13.2802 6.95692 13.0398 7.19733C12.7993 7.43774 12.4931 7.60146 12.1596 7.66779C11.8262 7.73413 11.4805 7.70009 11.1664 7.56999C10.8523 7.43989 10.5838 7.21957 10.3949 6.93689C10.2061 6.65421 10.1052 6.32186 10.1052 5.98188C10.1058 5.52614 10.287 5.08922 10.6093 4.76696C10.9316 4.44471 11.3685 4.26344 11.8242 4.26292V4.26283ZM11.8242 8.45088C11.3359 8.45088 10.8585 8.30608 10.4524 8.03477C10.0464 7.76347 9.72992 7.37785 9.54303 6.92667C9.35615 6.4755 9.30725 5.97904 9.40252 5.50008C9.4978 5.02112 9.73296 4.58116 10.0783 4.23585C10.4236 3.89054 10.8636 3.65539 11.3425 3.56012C11.8215 3.46486 12.3179 3.51377 12.7691 3.70066C13.2203 3.88755 13.6059 4.20404 13.8772 4.61009C14.1485 5.01614 14.2933 5.49353 14.2933 5.98188C14.2925 6.63647 14.0321 7.26404 13.5693 7.7269C13.1064 8.18977 12.4788 8.45013 11.8242 8.45088Z"
      fill="#165FE3"
    />
  </svg>
);

// Memoized components
interface ContactedByDisplayProps {
  contactedBy: any;
  showAll: boolean;
  onToggleShowAll: (show: boolean) => void;
}

const ContactedByDisplay = ({
  contactedBy,
  showAll,
  onToggleShowAll,
}: ContactedByDisplayProps) => {
  if (Array.isArray(contactedBy) && contactedBy.length > 0) {
    if (!showAll) {
      return (
        <span className="capitalize break-words text-sm text-[#666666] font-normal">
          {contactedBy[0]}
          {contactedBy.length > 1 && (
            <button
              className="text-blue-600 ml-1 underline"
              onClick={() => onToggleShowAll(true)}
            >
              {` + ${contactedBy.length - 1} more`}
            </button>
          )}
        </span>
      );
    } else {
      return (
        <div className="flex flex-wrap gap-x-1">
          {contactedBy.map((name: string, idx: number) => (
            <span key={idx} className="mr-1">
              {name}
              {idx < contactedBy.length - 1 ? "," : ""}
            </span>
          ))}
          <button
            className="text-blue-600 ml-1 underline"
            onClick={() => onToggleShowAll(false)}
          >
            Show less
          </button>
        </div>
      );
    }
  }

  return (
    <span className="capitalize break-words text-sm text-[#666666] font-normal">
      {contactedBy?.fname || "N/A"}
    </span>
  );
};

// Memoized interaction card row
interface InteractionCardRowProps {
  row: any;
  onRowClick?: (row: any) => void;
  channelOptions: ChannelOptions;
}

const InteractionCardRow = ({
  row,
  onRowClick,
  channelOptions,
}: InteractionCardRowProps) => {
  const [showAll, setShowAll] = useState(false);

  const handleRowClick = useCallback(() => {
    onRowClick?.(row.original);
  }, [onRowClick, row.original]);

  const handleQuotationClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(row.original.quotationURL, "_blank");
    },
    [row.original.quotationURL]
  );

  const toggleShowAll = useCallback((show: boolean) => {
    setShowAll(show);
  }, []);

  // Memoize computed values
  const computedValues = useMemo(() => {
    const createdAt = row?.original?.createdAt;
    const contactedByName = row?.original.contactedBy?.fname || "N/A";
    const channel = row?.original?.channel || "N/A";
    const channelType = row?.original?.channelType || "";
    const note = row?.original?.note || "N/A";

    const newSource = (channelOptions as ChannelOptions)?.[
      channelType?.toLowerCase()
    ]?.find(
      (item) =>
        item.value.toLowerCase() === channel.toLowerCase().replace(/\s/g, "")
    );

    return {
      createdAt,
      contactedByName,
      channel,
      channelType,
      note,
      newSource,
    };
  }, [row.original, channelOptions]);

  const { createdAt, contactedByName, channel, note, newSource } =
    computedValues;

  return (
    <div
      key={row.original.id}
      className="bg-white border border-[#F1F6F6] shadow-[0px_2px_4px_0px_#0000000D] w-full min-h-[220px] rounded-lg cursor-pointer mb-4 p-2.5"
      onClick={handleRowClick}
    >
      {/* Top Section */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex flex-col items-start gap-2 border-b border-[#C4D7F9] pb-2 w-full">
          <span className="text-sm text-[#333333] font-semibold">
            {createdAt}
          </span>
          <span className="text-sm text-[#999999] font-normal">
            {contactedByName}
          </span>
        </div>

        {row?.original?.quotationURL && (
          <button
            onClick={handleQuotationClick}
            className="text-blue-600"
            aria-label="Open quotation"
          >
            <QuotationIcon />
          </button>
        )}
      </div>

      {/* Contact Info */}
      <div className="flex flex-col gap-1">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 text-[12px] text-[#999999] font-normal">
          <span>Contacted by:</span>
          <ContactedByDisplay
            contactedBy={row?.original?.contactedBy}
            showAll={showAll}
            onToggleShowAll={toggleShowAll}
          />
        </div>

        <div className="flex items-center gap-1 text-[12px] text-[#999999] font-normal">
          <span>Contacted with:</span>
          <span className="capitalize whitespace-nowrap text-sm text-[#666666] font-normal">
            {row?.original?.contatedTo || "N/A"}
          </span>
        </div>
      </div>

      {/* Note */}
      <div className="rounded-[8px] mb-4">
        <p className="w-full h-auto p-3 font-normal text-sm bg-[#F9F9F9] rounded resize-none overflow-auto max-h-[100px] text-[#333333]">
          {note}
        </p>
      </div>

      {/* Footer */}
      <div className="flex border-t pt-3 px-3">
        <div className="flex items-center gap-4 w-1/2">
          <h3 className="text-gray-500 text-sm">{channel}</h3>
          {row?.original?.selfie && (
            <a
              href={row.original.selfie}
              download="image"
              target="_blank"
              rel="noopener noreferrer"
              className=""
            >
              <SelfieIcon />
            </a>
          )}
        </div>
        <div className="h-5 w-px bg-gray-300" />

        <div className="flex items-center justify-end w-1/2">
          {newSource?.icon && (
            <Img
              className="h-5 w-5"
              src={newSource.icon}
              alt="icon"
              width={20}
              height={20}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export function ForexOrderInteractionCard<TData, TValue>({
  columns,
  data,
  onRowClick,
  isPaginationVisible = false,
  isPageCountVisible = false,
  defaultSorting = [],
}: Readonly<InteractionCardProps<TData, TValue>>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination: { pageIndex, pageSize },
    },
    onSortingChange: setSorting,
    onPaginationChange: useCallback(
      (updater) => {
        const updated =
          typeof updater === "function"
            ? updater({ pageIndex, pageSize })
            : updater;
        setPageIndex(updated.pageIndex ?? pageIndex);
        setPageSize(updated.pageSize ?? pageSize);
      },
      [pageIndex, pageSize]
    ),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const paginatedRows = table?.getRowModel()?.rows;

  // Memoize channel options to prevent unnecessary re-computations
  const memoizedChannelOptions = useMemo(() => channel_options, []);

  const handlePreviousPage = useCallback(() => {
    table.previousPage();
  }, [table]);

  const handleNextPage = useCallback(() => {
    table.nextPage();
  }, [table]);

  // Memoize pagination info
  const paginationInfo = useMemo(
    () => ({
      canPreviousPage: table.getCanPreviousPage(),
      canNextPage: table.getCanNextPage(),
      currentPage: table.getState().pagination.pageIndex + 1,
      totalPages: table.getPageCount(),
    }),
    [table]
  );

  return (
    <div>
      {paginatedRows.map((row) => (
        <InteractionCardRow
          key={row.original.id}
          row={row}
          onRowClick={onRowClick}
          channelOptions={memoizedChannelOptions}
        />
      ))}

      {isPaginationVisible && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
              onClick={handlePreviousPage}
              disabled={!paginationInfo.canPreviousPage}
            >
              Prev
            </button>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
              onClick={handleNextPage}
              disabled={!paginationInfo.canNextPage}
            >
              Next
            </button>
          </div>
          {isPageCountVisible && (
            <div className="text-sm text-gray-500">
              Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
